import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import { useClearOverrides, useCreatePilot, useUpdateOverrides } from '@/hooks/use-accounts'
import { useAuth } from '@/lib/auth-context'
import type { AccountDetailResponse, AccountOverrides } from '@/lib/types'

const DEFAULT_TIER_VALUES: Record<string, number | string> = {
  rate_limit: 5,
  email_limit: 10000,
  send_rate: 20,
  max_recipients: 50,
}

const TIER_DEFAULTS: Record<string, Record<string, number | string>> = {
  free: { rate_limit: 5, email_limit: 10000, send_rate: 20, max_recipients: 50 },
  starter: { rate_limit: 10, email_limit: 25000, send_rate: 50, max_recipients: 50 },
  pro: { rate_limit: 50, email_limit: 75000, send_rate: 200, max_recipients: 100 },
  growth: { rate_limit: 200, email_limit: 300000, send_rate: 500, max_recipients: 500 },
  scale: { rate_limit: 500, email_limit: 2000000, send_rate: 1000, max_recipients: 1000 },
}

const OVERAGE_OPTIONS = ['allow', 'block', 'warn'] as const

interface OverridesTabProps {
  account: AccountDetailResponse
}

export function OverridesTab({ account }: OverridesTabProps): React.JSX.Element {
  const { isViewer } = useAuth()
  const updateOverrides = useUpdateOverrides()
  const clearOverrides = useClearOverrides()
  const createPilot = useCreatePilot()

  const defaults = TIER_DEFAULTS[account.tier] ?? DEFAULT_TIER_VALUES
  const hasOverrides = account.overrides !== null

  const [form, setForm] = useState<AccountOverrides>(account.overrides ?? {})
  const [showClearModal, setShowClearModal] = useState(false)
  const [showPilotModal, setShowPilotModal] = useState(false)
  const [pilotNotes, setPilotNotes] = useState('')

  const sixMonthsFromNow = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)
  const [pilotExpiry, setPilotExpiry] = useState(sixMonthsFromNow)

  function handleSave(): void {
    updateOverrides.mutate({ id: account.id, overrides: form })
  }

  function handleClear(): void {
    clearOverrides.mutate(account.id, {
      onSuccess: () => {
        setShowClearModal(false)
        setForm({})
      },
    })
  }

  function handleCreatePilot(): void {
    createPilot.mutate(
      { id: account.id, notes: pilotNotes, expires_at: pilotExpiry },
      { onSuccess: () => setShowPilotModal(false) },
    )
  }

  function updateField<K extends keyof AccountOverrides>(key: K, value: AccountOverrides[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  if (!hasOverrides && isViewer) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-12 text-center">
        <p className="text-text-secondary">No custom overrides. Using tier defaults.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {!hasOverrides && !isViewer && (
        <div className="rounded-xl border border-dashed border-surface-3 bg-surface-0 p-8 text-center">
          <p className="text-text-secondary">No custom overrides. Using tier defaults.</p>
          <div className="mt-4 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => setForm({})}
              className="rounded-lg bg-admin-600 px-4 py-2 text-sm font-medium text-white hover:bg-admin-700"
            >
              Add Overrides
            </button>
            <button
              type="button"
              onClick={() => setShowPilotModal(true)}
              className="rounded-lg border border-purple-300 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
            >
              Create Pilot
            </button>
          </div>
        </div>
      )}

      {(hasOverrides || Object.keys(form).length > 0) && (
        <OverrideForm
          form={form}
          defaults={defaults}
          onUpdate={updateField}
          onSave={handleSave}
          onClear={() => setShowClearModal(true)}
          onCreatePilot={() => setShowPilotModal(true)}
          isSaving={updateOverrides.isPending}
          isViewer={isViewer}
        />
      )}

      {/* Clear confirmation */}
      <Modal
        open={showClearModal}
        onClose={() => setShowClearModal(false)}
        title="Clear All Overrides"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            This will remove all custom overrides for{' '}
            <span className="font-medium text-text-primary">{account.user_email}</span>. They will
            revert to tier defaults.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowClearModal(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={clearOverrides.isPending}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {clearOverrides.isPending ? 'Clearing...' : 'Clear All Overrides'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create Pilot modal */}
      <Modal
        open={showPilotModal}
        onClose={() => setShowPilotModal(false)}
        title="Create Pilot Account"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            This sets up a pilot account with: email_limit=50,000, overage_policy=allow,
            ramp_bypass=true.
          </p>
          <div>
            <label htmlFor="pilot-notes" className="block text-sm font-medium text-text-primary">
              Notes
            </label>
            <textarea
              id="pilot-notes"
              value={pilotNotes}
              onChange={(e) => setPilotNotes(e.target.value)}
              placeholder="e.g., Newspaper pilot - 6 month free trial"
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
              rows={2}
            />
          </div>
          <div>
            <label htmlFor="pilot-expiry" className="block text-sm font-medium text-text-primary">
              Expires At
            </label>
            <input
              id="pilot-expiry"
              type="date"
              value={pilotExpiry}
              onChange={(e) => setPilotExpiry(e.target.value)}
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowPilotModal(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreatePilot}
              disabled={createPilot.isPending}
              className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {createPilot.isPending ? 'Creating...' : 'Create Pilot'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

interface OverrideFormProps {
  form: AccountOverrides
  defaults: Record<string, number | string>
  onUpdate: <K extends keyof AccountOverrides>(key: K, value: AccountOverrides[K]) => void
  onSave: () => void
  onClear: () => void
  onCreatePilot: () => void
  isSaving: boolean
  isViewer: boolean
}

function OverrideForm({
  form,
  defaults,
  onUpdate,
  onSave,
  onClear,
  onCreatePilot,
  isSaving,
  isViewer,
}: OverrideFormProps): React.JSX.Element {
  return (
    <div className="rounded-xl border border-purple-200 bg-surface-0 p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-primary">Custom Overrides</h3>
        {!isViewer && (
          <button
            type="button"
            onClick={onCreatePilot}
            className="rounded-lg border border-purple-300 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100"
          >
            Create Pilot
          </button>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField
          label="Rate Limit (req/s)"
          value={form.rate_limit}
          defaultVal={defaults.rate_limit}
          onChange={(v) => onUpdate('rate_limit', v)}
          disabled={isViewer}
        />
        <NumberField
          label="Email Limit (monthly)"
          value={form.email_limit}
          defaultVal={defaults.email_limit}
          onChange={(v) => onUpdate('email_limit', v)}
          disabled={isViewer}
        />
        <div>
          <label
            htmlFor="override-overage-policy"
            className="block text-xs font-medium text-text-secondary"
          >
            Overage Policy
          </label>
          <select
            id="override-overage-policy"
            value={form.overage_policy ?? ''}
            onChange={(e) =>
              onUpdate(
                'overage_policy',
                (e.target.value || undefined) as AccountOverrides['overage_policy'],
              )
            }
            disabled={isViewer}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary disabled:opacity-50 focus:border-admin-500 focus:outline-none"
          >
            <option value="">Tier default</option>
            {OVERAGE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="flex items-center gap-2 text-xs font-medium text-text-secondary">
            <input
              type="checkbox"
              checked={form.ramp_bypass ?? false}
              onChange={(e) => onUpdate('ramp_bypass', e.target.checked || undefined)}
              disabled={isViewer}
              className="rounded border-surface-3"
            />
            Ramp Bypass (skip IP warming)
          </label>
        </div>
        <NumberField
          label="Send Rate (msg/s)"
          value={form.send_rate}
          defaultVal={defaults.send_rate}
          onChange={(v) => onUpdate('send_rate', v)}
          disabled={isViewer}
        />
        <NumberField
          label="Max Recipients"
          value={form.max_recipients}
          defaultVal={defaults.max_recipients}
          onChange={(v) => onUpdate('max_recipients', v)}
          disabled={isViewer}
        />
        <div className="sm:col-span-2">
          <label
            htmlFor="override-force-transport"
            className="block text-xs font-medium text-text-secondary"
          >
            Force Transport
          </label>
          <input
            id="override-force-transport"
            type="text"
            value={form.force_transport ?? ''}
            onChange={(e) => onUpdate('force_transport', e.target.value || undefined)}
            placeholder="e.g., rust-smtp"
            disabled={isViewer}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary disabled:opacity-50 placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="override-notes" className="block text-xs font-medium text-text-secondary">
            Notes
          </label>
          <textarea
            id="override-notes"
            value={form.notes ?? ''}
            onChange={(e) => onUpdate('notes', e.target.value || undefined)}
            placeholder="Admin notes about this override"
            disabled={isViewer}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary disabled:opacity-50 placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
            rows={2}
          />
        </div>
        <div>
          <label
            htmlFor="override-expires-at"
            className="block text-xs font-medium text-text-secondary"
          >
            Expires At
          </label>
          <input
            id="override-expires-at"
            type="date"
            value={form.expires_at?.slice(0, 10) ?? ''}
            onChange={(e) => onUpdate('expires_at', e.target.value || undefined)}
            disabled={isViewer}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary disabled:opacity-50 focus:border-admin-500 focus:outline-none"
          />
        </div>
      </div>
      {!isViewer && (
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="rounded-lg bg-admin-600 px-4 py-2 text-sm font-medium text-white hover:bg-admin-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Overrides'}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Clear All Overrides
          </button>
        </div>
      )}
    </div>
  )
}

function NumberField({
  label,
  value,
  defaultVal,
  onChange,
  disabled,
}: {
  label: string
  value: number | undefined
  defaultVal: number | string | undefined
  onChange: (v: number | undefined) => void
  disabled: boolean
}): React.JSX.Element {
  const id = `override-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-text-secondary">
        {label}
      </label>
      <input
        id={id}
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        disabled={disabled}
        className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary disabled:opacity-50 focus:border-admin-500 focus:outline-none"
        placeholder={`Tier default: ${String(defaultVal ?? '-')}`}
      />
      <p className="mt-0.5 text-xs text-text-tertiary">Tier default: {String(defaultVal ?? '-')}</p>
    </div>
  )
}
